import { cva, type VariantProps } from 'class-variance-authority';
import { Check, Copy, List, ThumbsDown, ThumbsUp } from 'lucide-react';
import * as React from 'react';
import { forwardRef, type HTMLAttributes } from 'react';
import { Avatar, type AvatarProps } from '@/components/molecules/data/Avatar';
import { FileTypeIcon } from '@/components/molecules/files/FileTypeIcon';
import type { AIInteractionEvent } from '@/components/organisms/ai/_shared/interaction-context';
import { AIInteractionProvider } from '@/components/organisms/ai/_shared/interaction-context';
import { MarkdownRenderer } from '@/components/organisms/ai/_shared/markdown-renderer';
import { AITypingIndicator } from '@/components/organisms/ai/AITypingIndicator';
import { createSafeContext } from '@/lib/create-safe-context';
import { cn } from '@/lib/utils';

// =============================================================================
// Message Variants
// =============================================================================

const messageVariants = cva('flex items-start w-full', {
  variants: {
    type: {
      received: 'justify-start',
      sent: 'justify-end',
    },
  },
  defaultVariants: {
    type: 'received',
  },
});

const messageOverflowWrapClasses =
  'min-w-0 max-w-full break-words [overflow-wrap:anywhere]';

const messageBubbleVariants = cva(
  'flex min-w-0 max-w-[min(720px,100%)] flex-col gap-1 overflow-hidden px-4 py-3 rounded-2xl',
  {
    variants: {
      type: {
        received: 'bg-muted rounded-es-sm',
        sent: 'bg-primary text-primary-foreground rounded-ee-sm',
      },
    },
    defaultVariants: {
      type: 'received',
    },
  },
);

// =============================================================================
// Context for Composable API
// =============================================================================

interface AIMessageContextValue {
  /** Internal payload identity shared by compound Content and CopyButton. */
  copyContent?: React.ReactNode;
  setCopyContent: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  /** Message type */
  type: 'received' | 'sent';
  /** Whether sender is an AI agent */
  isAgent: boolean;
  /** Whether in compact mode */
  compact: boolean;
  /** Whether AI is typing */
  isTyping: boolean;
  /** Callback when thumbs up is clicked */
  onThumbsUp?: () => void;
  /** Callback when thumbs down is clicked */
  onThumbsDown?: () => void;
  /** Callback when copy is clicked */
  onCopy?: () => void;
  /** Callback when list/expand is clicked */
  onShowList?: () => void;
  /** Whether thumbs up is active */
  thumbsUpActive?: boolean;
  /** Whether thumbs down is active */
  thumbsDownActive?: boolean;
}

const [AIMessageContext, useAIMessageContext] =
  createSafeContext<AIMessageContextValue>('AIMessage');

// =============================================================================
// AIMessage.Bubble - Bubble wrapper for header + content
// =============================================================================

export interface AIMessageBubbleProps {
  /** Children (Header, Content, TypingIndicator) */
  children?: React.ReactNode;
  /** Additional class name */
  className?: string;
}

function AIMessageBubble({ children, className }: AIMessageBubbleProps) {
  const ctx = React.useContext(AIMessageContext);
  const type = ctx?.type ?? 'received';

  return (
    <div
      data-slot="ai-message-bubble"
      data-message-type={type}
      className={cn(messageBubbleVariants({ type }), className)}
    >
      {children}
    </div>
  );
}

AIMessageBubble.displayName = 'AIMessage.Bubble';

// =============================================================================
// Reaction Button (Internal)
// =============================================================================

interface ReactionButtonProps {
  icon: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  count?: number;
  /** Accessible label for the button */
  label?: string;
  className?: string;
}

function ReactionButton({
  icon,
  onClick,
  active,
  count,
  label,
  className,
}: ReactionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'flex items-center justify-center gap-1 px-2.5 py-2 min-w-11 min-h-11 rounded-full bg-muted hover:bg-accent transition-colors duration-150 ease-out motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
        active && 'bg-primary/10 text-primary',
        className,
      )}
    >
      {icon}
      {count !== undefined && count > 0 && (
        <span className="text-xs font-medium text-muted-foreground">
          {count}
        </span>
      )}
    </button>
  );
}

// =============================================================================
// Emoji Reaction Types
// =============================================================================

export interface EmojiReaction {
  emoji: string;
  count: number;
  reacted?: boolean;
}

// =============================================================================
// AIMessage.Reactions - Emoji reactions display
// =============================================================================

export interface AIMessageReactionsProps {
  /** Emoji reactions to display */
  reactions: EmojiReaction[];
  /** Callback when reaction is clicked */
  onReactionClick?: (emoji: string) => void;
  /** Additional class name */
  className?: string;
}

function AIMessageReactions({
  reactions,
  onReactionClick,
  className,
}: AIMessageReactionsProps) {
  if (!reactions?.length) return null;

  return (
    <div
      data-slot="ai-message-reactions"
      className={cn('flex items-center gap-1', className)}
    >
      {reactions.map((reaction) => (
        <button
          key={reaction.emoji}
          type="button"
          onClick={() => onReactionClick?.(reaction.emoji)}
          aria-label={`React with ${reaction.emoji}${reaction.count > 1 ? `, ${reaction.count} reactions` : ''}`}
          aria-pressed={reaction.reacted}
          className={cn(
            'flex items-center gap-1 px-2.5 py-1.5 min-h-11 rounded-full bg-muted hover:bg-accent text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
            reaction.reacted && 'bg-primary/10',
          )}
        >
          <span aria-hidden="true">{reaction.emoji}</span>
          {reaction.count > 1 && (
            <span className="text-xs text-muted-foreground">
              {reaction.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

AIMessageReactions.displayName = 'AIMessage.Reactions';

// =============================================================================
// AIMessage.ThumbsUp - Thumbs up button
// =============================================================================

export interface AIMessageThumbsUpProps {
  /** Click handler */
  onClick?: () => void;
  /** Whether active */
  active?: boolean;
  /** Additional class name */
  className?: string;
}

function AIMessageThumbsUp({
  onClick,
  active,
  className,
}: AIMessageThumbsUpProps) {
  return (
    <ReactionButton
      icon={
        <ThumbsUp className="size-4 text-muted-foreground" aria-hidden="true" />
      }
      onClick={onClick}
      active={active}
      label="Thumbs up"
      className={className}
    />
  );
}

AIMessageThumbsUp.displayName = 'AIMessage.ThumbsUp';

// =============================================================================
// AIMessage.ThumbsDown - Thumbs down button
// =============================================================================

export interface AIMessageThumbsDownProps {
  /** Click handler */
  onClick?: () => void;
  /** Whether active */
  active?: boolean;
  /** Additional class name */
  className?: string;
}

function AIMessageThumbsDown({
  onClick,
  active,
  className,
}: AIMessageThumbsDownProps) {
  return (
    <ReactionButton
      icon={
        <ThumbsDown
          className="size-4 text-muted-foreground"
          aria-hidden="true"
        />
      }
      onClick={onClick}
      active={active}
      label="Thumbs down"
      className={className}
    />
  );
}

AIMessageThumbsDown.displayName = 'AIMessage.ThumbsDown';

// =============================================================================
// AIMessage.CopyButton - Copy button
// =============================================================================

export interface AIMessageCopyButtonProps {
  /** Return the clipboard operation promise to acknowledge confirmed success. Void callbacks remain intent-only. */
  onClick?: () => void;
  /** Additional class name */
  className?: string;
}

function AIMessageCopyButton({ onClick, className }: AIMessageCopyButtonProps) {
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  const copyRequestRef = React.useRef(0);
  const copyContent = React.useContext(AIMessageContext)?.copyContent;

  // Replaced handlers represent replaced payloads; invalidate pending outcomes and timers.
  // biome-ignore lint/correctness/useExhaustiveDependencies: handler and content identify the copied payload.
  React.useEffect(() => {
    setCopied(false);
    return () => {
      copyRequestRef.current += 1;
      clearTimeout(timerRef.current);
    };
  }, [onClick, copyContent]);

  const handleClick = React.useCallback(async () => {
    const request = ++copyRequestRef.current;
    clearTimeout(timerRef.current);
    setCopied(false);
    try {
      const outcome: unknown = onClick?.();
      if (
        outcome === null ||
        (typeof outcome !== 'object' && typeof outcome !== 'function') ||
        !('then' in outcome) ||
        typeof outcome.then !== 'function'
      )
        return;
      await outcome;
      if (request !== copyRequestRef.current) return;
      setCopied(true);
      timerRef.current = setTimeout(() => {
        if (request === copyRequestRef.current) setCopied(false);
      }, 2000);
    } catch {
      // Failed clipboard writes are not success; consumers own error feedback.
      if (request === copyRequestRef.current) setCopied(false);
    }
  }, [onClick]);

  return (
    <ReactionButton
      icon={
        copied ? (
          <Check className="size-4 text-success" aria-hidden="true" />
        ) : (
          <Copy
            className="size-4 text-secondary-foreground"
            aria-hidden="true"
          />
        )
      }
      onClick={handleClick}
      label={copied ? 'Copied to clipboard' : 'Copy message'}
      className={className}
    />
  );
}

AIMessageCopyButton.displayName = 'AIMessage.CopyButton';

// =============================================================================
// AIMessage.ListButton - List/expand button
// =============================================================================

export interface AIMessageListButtonProps {
  /** Click handler */
  onClick?: () => void;
  /** Additional class name */
  className?: string;
}

function AIMessageListButton({ onClick, className }: AIMessageListButtonProps) {
  return (
    <ReactionButton
      icon={
        <List className="size-4 text-secondary-foreground" aria-hidden="true" />
      }
      onClick={onClick}
      label="Show list"
      className={className}
    />
  );
}

AIMessageListButton.displayName = 'AIMessage.ListButton';

// =============================================================================
// AIMessage.Actions - Action toolbar container
// =============================================================================

export interface AIMessageActionsProps {
  /** Children (action buttons) */
  children?: React.ReactNode;
  /** Additional class name */
  className?: string;
}

function AIMessageActions({ children, className }: AIMessageActionsProps) {
  const ctx = React.useContext(AIMessageContext);

  return (
    <div
      data-slot="ai-message-actions"
      role="toolbar"
      aria-label="Message actions"
      className={cn(
        'flex items-center gap-0.5 rounded-full overflow-hidden',
        className,
      )}
    >
      {children ??
        (ctx && (
          <>
            <AIMessageThumbsUp
              onClick={ctx.onThumbsUp}
              active={ctx.thumbsUpActive}
            />
            <AIMessageThumbsDown
              onClick={ctx.onThumbsDown}
              active={ctx.thumbsDownActive}
            />
            <AIMessageCopyButton onClick={ctx.onCopy} />
            <AIMessageListButton onClick={ctx.onShowList} />
          </>
        ))}
    </div>
  );
}

AIMessageActions.displayName = 'AIMessage.Actions';

// =============================================================================
// AIMessage.TypingIndicator - Stationary opacity dots
// =============================================================================

export interface AIMessageTypingIndicatorProps {
  /** Additional class name */
  className?: string;
}

function AIMessageTypingIndicator({
  className,
}: AIMessageTypingIndicatorProps) {
  return (
    <div
      data-slot="ai-message-typing-indicator"
      className={cn(
        'flex items-center gap-1 motion-reduce:animate-none',
        className,
      )}
    >
      <AITypingIndicator size="sm" color="var(--foreground)" />
    </div>
  );
}

AIMessageTypingIndicator.displayName = 'AIMessage.TypingIndicator';

// =============================================================================
// Attachment Types
// =============================================================================

export interface Attachment {
  id: string;
  name: string;
  size: string;
  /** File extension (e.g., 'pdf', 'jpg', 'doc') */
  extension?: string;
  url?: string;
  thumbnailUrl?: string;
}

// =============================================================================
// AIMessage.AttachmentPreview - Single attachment preview
// =============================================================================

export interface AIMessageAttachmentPreviewProps {
  /** Attachment data */
  attachment: Attachment;
  /** Message type for styling */
  messageType?: 'received' | 'sent';
  /** Click handler */
  onClick?: () => void;
  /** Additional class name */
  className?: string;
}

function AIMessageAttachmentPreview({
  attachment,
  messageType = 'received',
  onClick,
  className,
}: AIMessageAttachmentPreviewProps) {
  // Derive extension from attachment.extension or extract from filename
  const getExtension = () => {
    if (attachment.extension) {
      return attachment.extension;
    }
    const parts = attachment.name.split('.');
    if (parts.length > 1) {
      return parts[parts.length - 1];
    }
    return '';
  };

  const extension = getExtension();

  const bubbleClasses =
    messageType === 'sent'
      ? 'bg-primary/10 border border-primary-foreground/20 rounded-2xl'
      : 'bg-muted rounded-2xl';

  return (
    <button
      type="button"
      data-slot="ai-message-attachment-preview"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 p-3 min-h-11 hover:bg-accent transition-colors w-full max-w-[320px] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
        bubbleClasses,
        className,
      )}
    >
      {attachment.thumbnailUrl ? (
        <img
          src={attachment.thumbnailUrl}
          alt=""
          className="size-10 rounded-md object-cover"
        />
      ) : (
        <FileTypeIcon extension={extension} size="md" className="shrink-0" />
      )}
      <div className="flex flex-col items-start min-w-0">
        <span className="text-sm font-medium text-secondary-foreground truncate w-full">
          {attachment.name}
        </span>
        <span className="text-sm text-muted-foreground">{attachment.size}</span>
      </div>
    </button>
  );
}

AIMessageAttachmentPreview.displayName = 'AIMessage.AttachmentPreview';

// =============================================================================
// AIMessage.Attachments - Attachments container
// =============================================================================

export interface AIMessageAttachmentsProps {
  /** Attachments to display */
  attachments: Attachment[];
  /** Message type for styling */
  messageType?: 'received' | 'sent';
  /** Callback when attachment is clicked */
  onAttachmentClick?: (attachment: Attachment) => void;
  /** Additional class name */
  className?: string;
}

function AIMessageAttachments({
  attachments,
  messageType = 'received',
  onAttachmentClick,
  className,
}: AIMessageAttachmentsProps) {
  if (!attachments?.length) return null;

  return (
    <div
      data-slot="ai-message-attachments"
      className={cn('flex flex-col gap-2 mt-1', className)}
    >
      {attachments.map((attachment) => (
        <AIMessageAttachmentPreview
          key={attachment.id}
          attachment={attachment}
          messageType={messageType}
          onClick={() => onAttachmentClick?.(attachment)}
        />
      ))}
    </div>
  );
}

AIMessageAttachments.displayName = 'AIMessage.Attachments';

// =============================================================================
// AIMessage.Avatar - Avatar with agent indicator
// =============================================================================

export interface AIMessageAvatarProps extends AvatarProps {
  /** Whether sender is an agent */
  isAgent?: boolean;
}

function AIMessageAvatar({ isAgent, ...props }: AIMessageAvatarProps) {
  return (
    <Avatar
      data-slot="ai-message-avatar"
      {...props}
      size="md"
      indicator={isAgent ? 'verified' : props.indicator}
    />
  );
}

AIMessageAvatar.displayName = 'AIMessage.Avatar';

// =============================================================================
// AIMessage.Content - Message content with markdown support
// =============================================================================

export interface AIMessageContentProps {
  /** Message content */
  children?: React.ReactNode;
  /** Content as string (alternative to children) */
  content?: string;
  /** Whether to render content as markdown. Defaults to true for received, false for sent. */
  markdown?: boolean;
  /** Callback for interactive component events */
  onInteraction?: (event: AIInteractionEvent) => void;
  /** Additional class name */
  className?: string;
}

function AIMessageContent({
  children,
  content,
  markdown,
  onInteraction,
  className,
}: AIMessageContentProps) {
  const ctx = React.useContext(AIMessageContext);
  const type = ctx?.type ?? 'received';
  const isSent = type === 'sent';

  const shouldRenderMarkdown = markdown ?? type === 'received';
  const textContent = content ?? (typeof children === 'string' ? children : '');
  const variant = isSent ? 'inverted' : 'default';
  const setCopyContent = ctx?.setCopyContent;
  const copyContent = content ?? children;
  React.useLayoutEffect(() => {
    setCopyContent?.(() => copyContent);
    return () => setCopyContent?.(undefined);
  }, [setCopyContent, copyContent]);

  return (
    <div
      data-slot="ai-message-content"
      className={cn('w-full', messageOverflowWrapClasses, className)}
    >
      {shouldRenderMarkdown && textContent ? (
        <AIInteractionProvider onInteraction={onInteraction}>
          <MarkdownRenderer
            variant={variant}
            className={cn(
              'text-base',
              isSent ? 'text-primary-foreground' : 'text-foreground',
            )}
          >
            {textContent}
          </MarkdownRenderer>
        </AIInteractionProvider>
      ) : textContent ? (
        <p
          className={cn(
            'text-sm leading-5 whitespace-pre-wrap',
            messageOverflowWrapClasses,
            isSent ? 'text-primary-foreground' : 'text-foreground',
          )}
        >
          {textContent}
        </p>
      ) : (
        children
      )}
    </div>
  );
}

AIMessageContent.displayName = 'AIMessage.Content';

// =============================================================================
// AIMessage.Header - Header with name and timestamp
// =============================================================================

export interface AIMessageHeaderProps {
  /** Sender name */
  senderName?: string;
  /** Timestamp string */
  timestamp?: string;
  /** Show avatar slot */
  avatar?: React.ReactNode;
  /** Additional class name */
  className?: string;
}

function AIMessageHeader({
  senderName,
  timestamp,
  avatar,
  className,
}: AIMessageHeaderProps) {
  const ctx = React.useContext(AIMessageContext);
  const isSent = ctx?.type === 'sent';
  const compact = ctx?.compact ?? false;

  if (compact) return null;

  return (
    <div
      data-slot="ai-message-header"
      className={cn('flex items-center justify-between w-full', className)}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {avatar && <div className="shrink-0">{avatar}</div>}
        <span
          className={cn(
            'text-sm font-medium truncate',
            isSent ? 'text-primary-foreground' : 'text-secondary-foreground',
          )}
        >
          {isSent ? (senderName ?? 'You') : senderName}
        </span>
      </div>
      {timestamp && (
        <span
          className={cn(
            'text-xs shrink-0',
            isSent ? 'text-primary-foreground/90' : 'text-muted-foreground',
          )}
        >
          {timestamp}
        </span>
      )}
    </div>
  );
}

AIMessageHeader.displayName = 'AIMessage.Header';

// =============================================================================
// AIMessage.Root - Root container for composable API
// =============================================================================

export interface AIMessageRootProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  /** Message type */
  type?: 'received' | 'sent';
  /** Whether sender is an AI agent */
  isAgent?: boolean;
  /** Whether in compact mode */
  compact?: boolean;
  /** Whether AI is typing */
  isTyping?: boolean;
  /** Callback when thumbs up is clicked */
  onThumbsUp?: () => void;
  /** Callback when thumbs down is clicked */
  onThumbsDown?: () => void;
  /** Callback when copy is clicked */
  onCopy?: () => void;
  /** Callback when list/expand is clicked */
  onShowList?: () => void;
  /** Whether thumbs up is active */
  thumbsUpActive?: boolean;
  /** Whether thumbs down is active */
  thumbsDownActive?: boolean;
  /** Children */
  children?: React.ReactNode;
  /** Additional class name */
  className?: string;
}

const AIMessageRoot = forwardRef<HTMLDivElement, AIMessageRootProps>(
  (
    {
      type = 'received',
      isAgent = false,
      compact = false,
      isTyping = false,
      onThumbsUp,
      onThumbsDown,
      onCopy,
      onShowList,
      thumbsUpActive,
      thumbsDownActive,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const isSent = type === 'sent';
    const [copyContent, setCopyContent] = React.useState<React.ReactNode>();

    const contextValue = React.useMemo<AIMessageContextValue>(
      () => ({
        copyContent,
        setCopyContent,
        type,
        isAgent,
        compact,
        isTyping,
        onThumbsUp,
        onThumbsDown,
        onCopy,
        onShowList,
        thumbsUpActive,
        thumbsDownActive,
      }),
      [
        copyContent,
        type,
        isAgent,
        compact,
        isTyping,
        onThumbsUp,
        onThumbsDown,
        onCopy,
        onShowList,
        thumbsUpActive,
        thumbsDownActive,
      ],
    );

    return (
      <AIMessageContext.Provider value={contextValue}>
        <div
          ref={ref}
          data-slot="ai-message"
          className={cn(
            messageVariants({ type }),
            isSent ? 'pe-2' : 'ps-0',
            className,
          )}
          {...props}
        >
          <div
            className={cn(
              'flex min-w-0 max-w-full flex-col gap-1.5 items-start',
              isSent && 'items-end',
            )}
          >
            {children}
          </div>
        </div>
      </AIMessageContext.Provider>
    );
  },
);

AIMessageRoot.displayName = 'AIMessage.Root';

// =============================================================================
// Main AIMessage Component (Monolithic API)
// =============================================================================

export interface AIMessageProps
  extends VariantProps<typeof messageVariants>,
    Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  /** Message type - 'received' for incoming, 'sent' for outgoing messages */
  type?: 'received' | 'sent';
  /** Message content */
  content?: string;
  /** Sender name */
  senderName?: string;
  /** Timestamp */
  timestamp?: string;
  /** Avatar — pass AvatarProps object or a React element */
  avatar?: AvatarProps | React.ReactElement;
  /** Whether the sender is an AI agent */
  isAgent?: boolean;
  /** Whether message is in compact mode (no avatar on subsequent messages) */
  compact?: boolean;
  /** Whether the AI is currently typing */
  isTyping?: boolean;
  /** Whether to render content as markdown. Defaults to true for received, false for sent. */
  markdown?: boolean;
  /** Callback when an interactive component triggers an interaction */
  onInteraction?: (event: AIInteractionEvent) => void;
  /** Attachments */
  attachments?: Attachment[];
  /** Emoji reactions */
  reactions?: EmojiReaction[];
  /** Whether to show action toolbar (for agent messages) */
  showActions?: boolean;
  /** Callback when thumbs up is clicked */
  onThumbsUp?: () => void;
  /** Callback when thumbs down is clicked */
  onThumbsDown?: () => void;
  /** Callback when copy is clicked */
  onCopy?: () => void;
  /** Callback when list/expand is clicked */
  onShowList?: () => void;
  /** Callback when emoji reaction is clicked */
  onReactionClick?: (emoji: string) => void;
  /** Callback when attachment is clicked */
  onAttachmentClick?: (attachment: Attachment) => void;
  /** Whether thumbs up is active */
  thumbsUpActive?: boolean;
  /** Whether thumbs down is active */
  thumbsDownActive?: boolean;
  /** Additional class name */
  className?: string;
}

const AIMessageDefault = forwardRef<HTMLDivElement, AIMessageProps>(
  (
    {
      type = 'received',
      content,
      senderName,
      timestamp,
      avatar,
      isAgent = false,
      compact = false,
      isTyping = false,
      markdown,
      onInteraction,
      attachments,
      reactions,
      showActions = false,
      onThumbsUp,
      onThumbsDown,
      onCopy,
      onShowList,
      onReactionClick,
      onAttachmentClick,
      thumbsUpActive,
      thumbsDownActive,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <AIMessageRoot
        ref={ref}
        type={type}
        isAgent={isAgent}
        compact={compact}
        isTyping={isTyping}
        onThumbsUp={onThumbsUp}
        onThumbsDown={onThumbsDown}
        onCopy={onCopy}
        onShowList={onShowList}
        thumbsUpActive={thumbsUpActive}
        thumbsDownActive={thumbsDownActive}
        className={className}
        {...props}
      >
        {type !== 'sent' &&
          !compact &&
          avatar &&
          (React.isValidElement(avatar) ? (
            avatar
          ) : (
            <AIMessageAvatar {...(avatar as AvatarProps)} isAgent={isAgent} />
          ))}

        <AIMessageBubble>
          {(type !== 'sent' || senderName || timestamp) && (
            <AIMessageHeader senderName={senderName} timestamp={timestamp} />
          )}

          {isTyping ? (
            <AIMessageTypingIndicator />
          ) : (
            content && (
              <AIMessageContent
                content={content}
                markdown={markdown}
                onInteraction={onInteraction}
              />
            )
          )}
        </AIMessageBubble>

        {!isTyping && attachments && attachments.length > 0 && (
          <AIMessageAttachments
            attachments={attachments}
            messageType={type ?? 'received'}
            onAttachmentClick={onAttachmentClick}
          />
        )}

        {!isTyping && (showActions || reactions?.length) && (
          <div className="flex items-center gap-1">
            {showActions && isAgent && <AIMessageActions />}
            {reactions && reactions.length > 0 && (
              <AIMessageReactions
                reactions={reactions}
                onReactionClick={onReactionClick}
              />
            )}
          </div>
        )}
      </AIMessageRoot>
    );
  },
);

AIMessageDefault.displayName = 'AIMessage';

// =============================================================================
// Compound Component (Dual-API via Object.assign)
// =============================================================================

/**
 * AIMessage - Dual-API component for AI chat messages.
 *
 * Supports two usage patterns:
 *
 * 1. **Monolithic API (batteries-included)**:
 * ```tsx
 * <AIMessage
 *   content="Hello!"
 *   senderName="Assistant"
 *   avatar={{ src: '...' }}
 *   isAgent
 *   showActions
 * />
 * ```
 *
 * 2. **Composable API (full customization)**:
 * ```tsx
 * <AIMessage.Root type="received" isAgent>
 *   <AIMessage.Avatar src="..." isAgent />
 *   <AIMessage.Bubble>
 *     <AIMessage.Header senderName="Assistant" timestamp="Now" />
 *     <AIMessage.Content markdown>
 *       Hello!
 *     </AIMessage.Content>
 *   </AIMessage.Bubble>
 *   <AIMessage.Actions>
 *     <AIMessage.ThumbsUp onClick={...} />
 *     <AIMessage.ThumbsDown onClick={...} />
 *     <AIMessage.CopyButton onClick={...} />
 *   </AIMessage.Actions>
 * </AIMessage.Root>
 * ```
 */
const AIMessage = Object.assign(AIMessageDefault, {
  Root: AIMessageRoot,
  Bubble: AIMessageBubble,
  Header: AIMessageHeader,
  Avatar: AIMessageAvatar,
  Content: AIMessageContent,
  Actions: AIMessageActions,
  Reactions: AIMessageReactions,
  Attachments: AIMessageAttachments,
  AttachmentPreview: AIMessageAttachmentPreview,
  TypingIndicator: AIMessageTypingIndicator,
  CopyButton: AIMessageCopyButton,
  ThumbsUp: AIMessageThumbsUp,
  ThumbsDown: AIMessageThumbsDown,
  ListButton: AIMessageListButton,
});

// =============================================================================
// Named Exports
// =============================================================================

// Type exports
export type { AIMessageRootProps as AIMessageComposableProps };
export {
  AIMessage,
  AIMessageActions,
  AIMessageAttachmentPreview,
  AIMessageAttachments,
  AIMessageAvatar,
  AIMessageBubble,
  AIMessageContent,
  AIMessageCopyButton,
  AIMessageHeader,
  AIMessageListButton,
  AIMessageReactions,
  // Composable subcomponents
  AIMessageRoot,
  AIMessageThumbsDown,
  AIMessageThumbsUp,
  AIMessageTypingIndicator,
  // Variants
  messageBubbleVariants,
  messageVariants,
  // Context hook for custom subcomponents
  useAIMessageContext,
};
